import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errText = (e, fb) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : d?.message || fb; };

function ProfileCard() {
    const { user, refreshUser } = useAuth();
    const [form, setForm] = useState({ first_name: '', last_name: '', phone: '' });
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (user) setForm({ first_name: user.first_name || '', last_name: user.last_name || '', phone: user.phone || '' }); }, [user]);
    const save = async () => {
        setSaving(true);
        try { await axios.patch(`${API}/auth/me`, form); await refreshUser(); toast.success('Perfil actualizado'); }
        catch (e) { toast.error(errText(e, 'No se pudo guardar')); }
        finally { setSaving(false); }
    };
    return (
        <Card className="card-aviation" data-testid="settings-profile">
            <CardHeader><CardTitle className="font-['Barlow_Condensed'] uppercase">Perfil</CardTitle></CardHeader>
            <CardContent className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Nombre</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="settings-first-name" /></div>
                    <div className="space-y-1.5"><Label>Apellido</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="settings-last-name" /></div>
                    <div className="space-y-1.5"><Label>Teléfono</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="settings-phone" /></div>
                    <div className="space-y-1.5"><Label>Correo</Label><Input value={user?.email || ''} disabled data-testid="settings-email" /></div>
                </div>
                <p className="text-xs text-muted-foreground">El correo y el rol los cambia un administrador desde Equipo.</p>
                <Button onClick={save} disabled={saving} data-testid="settings-save-profile">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar perfil</Button>
            </CardContent>
        </Card>
    );
}

function PasswordCard() {
    const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
    const [saving, setSaving] = useState(false);
    const save = async () => {
        if (form.new_password !== form.confirm) { toast.error('Las contraseñas nuevas no coinciden'); return; }
        setSaving(true);
        try {
            const r = await axios.post(`${API}/auth/password/change`, { current_password: form.current_password, new_password: form.new_password });
            if (r.data.access_token) { localStorage.setItem('aeroops_token', r.data.access_token); axios.defaults.headers.common['Authorization'] = `Bearer ${r.data.access_token}`; }
            setForm({ current_password: '', new_password: '', confirm: '' });
            toast.success('Contraseña actualizada');
        } catch (e) { toast.error(errText(e, 'No se pudo cambiar la contraseña')); }
        finally { setSaving(false); }
    };
    return (
        <Card className="card-aviation" data-testid="settings-password">
            <CardHeader><CardTitle className="font-['Barlow_Condensed'] uppercase">Cambiar contraseña</CardTitle></CardHeader>
            <CardContent className="space-y-3">
                <div className="grid sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5"><Label>Contraseña actual</Label><Input type="password" value={form.current_password} onChange={e => setForm({ ...form, current_password: e.target.value })} data-testid="settings-current-password" /></div>
                    <div className="space-y-1.5"><Label>Nueva contraseña</Label><Input type="password" value={form.new_password} onChange={e => setForm({ ...form, new_password: e.target.value })} data-testid="settings-new-password" /></div>
                    <div className="space-y-1.5"><Label>Confirmar nueva</Label><Input type="password" value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} data-testid="settings-confirm-password" /></div>
                </div>
                <p className="text-xs text-muted-foreground">Mínimo 8 caracteres, con mayúscula, minúscula y número.</p>
                <Button onClick={save} disabled={saving || !form.current_password || !form.new_password} data-testid="settings-save-password">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Cambiar contraseña</Button>
            </CardContent>
        </Card>
    );
}

export default function SettingsPage() {
    return (
        <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="settings-page">
            <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed'] flex items-center gap-2"><Settings className="w-7 h-7 text-primary" />Configuración</h1>
            <ProfileCard />
            <PasswordCard />
        </div>
    );
}
